import { useCallback, useRef, useState, type DragEvent } from "react";
import { requestPresignedUrl, uploadToS3, type PresignedUrlResponse } from "../../lib/upload-api";
import styles from "./AudioUploader.module.css";

type UploadState = "idle" | "uploading" | "complete" | "error";

const MAX_FILE_SIZE = 500 * 1024 * 1024; // 500 MB
const ACCEPTED_TYPES = [
  "audio/mpeg",
  "audio/mp3",
  "audio/mp4",
  "audio/x-m4a",
  "audio/wav",
  "audio/x-wav",
  "audio/ogg",
  "audio/flac",
  "audio/aac",
];

interface AudioUploaderProps {
  orgId: string;
  podcastId: string;
  /** Called with the S3 key when upload completes */
  onComplete: (s3Key: string, fileName: string) => void;
  /** Called when the user removes a completed upload */
  onRemove?: () => void;
  /** Existing S3 key (shows completed state) */
  existingKey?: string;
  existingFileName?: string;
  disabled?: boolean;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function AudioUploader({
  orgId,
  podcastId,
  onComplete,
  onRemove,
  existingKey,
  existingFileName,
  disabled = false,
}: AudioUploaderProps) {
  const [state, setState] = useState<UploadState>(existingKey ? "complete" : "idle");
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [fileName, setFileName] = useState(existingFileName || "");
  const [s3Key, setS3Key] = useState(existingKey || "");
  const [dragOver, setDragOver] = useState(false);

  const abortRef = useRef<AbortController | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const validateFile = useCallback((file: File): string | null => {
    if (
      !ACCEPTED_TYPES.includes(file.type) &&
      !file.name.match(/\.(mp3|m4a|wav|ogg|flac|aac|mp4)$/i)
    ) {
      return "Unsupported file type. Please upload an audio file (MP3, M4A, WAV, OGG, FLAC, AAC).";
    }
    if (file.size > MAX_FILE_SIZE) {
      return `File too large (${formatFileSize(file.size)}). Maximum size is ${formatFileSize(MAX_FILE_SIZE)}.`;
    }
    if (file.size === 0) {
      return "File is empty.";
    }
    return null;
  }, []);

  const startUpload = useCallback(
    async (file: File) => {
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        setState("error");
        return;
      }

      setState("uploading");
      setProgress(0);
      setError(null);
      setFileName(file.name);

      const abort = new AbortController();
      abortRef.current = abort;

      try {
        const presigned: PresignedUrlResponse = await requestPresignedUrl(orgId, podcastId, file);

        await uploadToS3(presigned.uploadUrl, file, {
          onProgress: setProgress,
          signal: abort.signal,
        });

        setS3Key(presigned.s3Key);
        setState("complete");
        setProgress(100);
        onComplete(presigned.s3Key, file.name);
      } catch (err: unknown) {
        if (err instanceof DOMException && err.name === "AbortError") {
          setState("idle");
          return;
        }
        setError(err instanceof Error ? err.message : "Upload failed");
        setState("error");
      } finally {
        abortRef.current = null;
      }
    },
    [orgId, podcastId, onComplete, validateFile],
  );

  const handleFileSelect = useCallback(
    (files: FileList | null) => {
      const file = files?.[0];
      if (file) startUpload(file);
    },
    [startUpload],
  );

  const handleDrop = useCallback(
    (e: DragEvent<HTMLButtonElement>) => {
      e.preventDefault();
      setDragOver(false);
      if (disabled || state === "uploading") return;
      handleFileSelect(e.dataTransfer.files);
    },
    [disabled, state, handleFileSelect],
  );

  const handleCancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const handleRemove = useCallback(() => {
    setState("idle");
    setS3Key("");
    setFileName("");
    setProgress(0);
    setError(null);
    onRemove?.();
  }, [onRemove]);

  const handleRetry = useCallback(() => {
    setState("idle");
    setError(null);
    inputRef.current?.click();
  }, []);

  // Idle / drop zone
  if (state === "idle") {
    return (
      <div className={styles.container}>
        <button
          type="button"
          className={`${styles.dropzone} ${dragOver ? styles.dropzoneDragOver : ""} ${disabled ? styles.dropzoneDisabled : ""}`}
          onDragOver={(e) => {
            e.preventDefault();
            if (!disabled) setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => !disabled && inputRef.current?.click()}
          disabled={disabled}
        >
          <span className={styles.dropzoneIcon}>🎵</span>
          <p className={styles.dropzoneText}>
            <strong>Click to upload</strong> or drag and drop
          </p>
          <p className={styles.dropzoneHint}>
            MP3, M4A, WAV, OGG, FLAC, AAC — up to {formatFileSize(MAX_FILE_SIZE)}
          </p>
          <input
            ref={inputRef}
            type="file"
            accept="audio/*"
            style={{ display: "none" }}
            onChange={(e) => handleFileSelect(e.target.files)}
            disabled={disabled}
          />
        </button>
        {error && <div className={styles.error}>{error}</div>}
      </div>
    );
  }

  // Uploading
  if (state === "uploading") {
    return (
      <div className={styles.container}>
        <div className={styles.progressContainer}>
          <div className={styles.progressHeader}>
            <span className={styles.fileName}>{fileName}</span>
            <span className={styles.progressPercent}>{progress}%</span>
          </div>
          <div className={styles.progressBar}>
            <div className={styles.progressFill} style={{ width: `${progress}%` }} />
          </div>
          <div className={styles.progressActions}>
            <button type="button" className={styles.cancelButton} onClick={handleCancel}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Complete
  if (state === "complete") {
    return (
      <div className={styles.container}>
        <div className={styles.complete}>
          <span className={styles.completeIcon}>✓</span>
          <span className={styles.completeText}>{fileName || s3Key}</span>
          <button type="button" className={styles.removeButton} onClick={handleRemove}>
            Remove
          </button>
        </div>
      </div>
    );
  }

  // Error
  return (
    <div className={styles.container}>
      <div className={styles.error}>{error}</div>
      <div className={styles.progressActions}>
        <button type="button" className={styles.retryButton} onClick={handleRetry}>
          Try again
        </button>
      </div>
    </div>
  );
}
